<script setup>
import DropDownMenu from '@/components/Atoms/DropDownMenu.vue'
import DropDownMenuItem from '@/components/Atoms/DropDownMenuItem.vue'
import { ref, defineProps, computed, watch, nextTick } from 'vue'
import { useNotification } from "@kyvg/vue3-notification";
import { useStore } from 'vuex'
import MediaflowService from '@/services/MediaflowService'
import FormButton from '../Atoms/FormButton.vue'
import Alert from '../Atoms/Alert.vue'
import FormInput from '../Atoms/FormInput.vue'
import IconBase from '@/components/icon/IconBase.vue'
import IconChevronDown from '@/components/icon/IconChevronDown.vue'
import IconPlus from '@/components/icon/IconPlus.vue'
import IconCross from '../icon/IconCross.vue'
import IconBin from '../icon/IconBin.vue'
import IconMediaflowSingleTime from '../icon/IconMediaflowSingleTime.vue'
import IconMediaflowRecurringTrigger from '../icon/IconMediaflowRecurringTrigger.vue'
import IconCalendar from '../icon/IconCalendar.vue'
import IconClock from '../icon/IconClock.vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import Sidebar from '../modals/Sidebar'

const props = defineProps({
  mediaflowId: {
    type: String,
    default: '',
    required: true
  }
})

const store = useStore()
const { notify } = useNotification()

const isOpen = ref(false)

const sidebar = ref()
const error = ref(null)
const isEditMode = ref(false)
const editingScheduleId = ref(null)
const existingSchedules = ref([])
const overlapWarning = ref(null)

const mediaflow = computed(() => store.getters['Mediaflows/mediaflow'])

const scheduleTypeDropdown = ref(null);
const recurringReturnFlowDropdown = ref(null);
const singleReturnFlowDropdown = ref(null);
const flowDropdown = ref(null);
const rotationReturnFlowDropdown = ref(null);
const slotReturnFlowDropdown = ref(null);
const slotIntervalDropdown = ref(null);
const slotCustomUnitDropdown = ref(null);
const repeatIntervalDropdown = ref(null);
const customIntervalUnitDropdown = ref(null);

const schedule = ref({
  name: '',
  type: null,
  // Single Time fields
  flowId: null,
  startTime: null,
  duration: '30',
  returnFlowId: null,
  // Recurring fields
  recurringFlowId: null,
  recurringStartTime: null,
  recurringEndTime: null,
  repeatInterval: null,
  customIntervalValue: '',
  customIntervalUnit: 'Seconds',
  recurringDuration: '30',
  recurringReturnFlowId: null,
  // Multi Ad Block fields
  multiStartTime: null,
  multiReturnFlowId: null,
  multiAds: [
    {
      id: Date.now(),
      flowId: null,
      duration: '30'
    }
  ],
  // Scene Rotation fields
  rotationInterval: null,
  rotationCustomValue: '',
  rotationCustomUnit: 'Seconds',
  rotationDuration: '30',
  rotationReturnFlowId: null,
  rotationScenes: [
    {
      id: Date.now() + 1,
      name: ''
    }
  ],
  // Time Slot Block fields
  slotReturnFlowId: null,
  slotStartTime: null,
  slotEndTime: null,
  slotInterval: null,
  slotCustomValue: '',
  slotCustomUnit: 'Seconds',
  slotDuration: '30'
})

const scheduleTypes = [
  {
    value: 'single-time',
    label: 'Single Time',
    description: 'Play a specific ad scene at a fixed time',
    icon: IconMediaflowSingleTime
  },
  {
    value: 'recurring',
    label: 'Recurring',
    description: 'Play a specific ad scene on a recurring interval',
    icon: IconMediaflowRecurringTrigger
  },
  // {
  //   value: 'multi-ad-block',
  //   label: 'Multi Ad Block',
  //   description: 'Play a sequence of ad scenes starting at a specific time',
  //   icon: IconMediaflowMultiAdBlock
  // },
  // {
  //   value: 'scene-rotation',
  //   label: 'Scene Rotation',
  //   description: 'Rotate through a list of ad scenes on a fixed interval',
  //   icon: IconMediaflowSceneRotation
  // },
  // {
  //   value: 'time-slot-block',
  //   label: 'Time Slot Block',
  //   description: 'Show ads at fixed intervals within a time window',
  //   icon: IconMediaflowTimeSlotBlock
  // }
]

const selectedScheduleType = computed(() => {
  return scheduleTypes.find(t => t.value === schedule.value.type)
})

// Minimum date is current date/time to prevent selecting past dates
const minDate = computed(() => {
  return new Date()
})

const repeatIntervalOptions = [
  'Every 5 minutes',
  'Every 10 minutes',
  'Every 15 minutes',
  'Every 30 minutes',
  'Every 1 hour',
  'Every 2 hours',
  'Custom interval'
]

const addMultiAd = () => {
  schedule.value.multiAds.push({
    id: Date.now(),
    flowId: null,
    duration: '30'
  })
}

const removeMultiAd = (id) => {
  schedule.value.multiAds = schedule.value.multiAds.filter(ad => ad.id !== id)
}

const addRotationScene = () => {
  schedule.value.rotationScenes.push({
    id: Date.now(),
    name: ''
  })
}

const removeRotationScene = (id) => {
  schedule.value.rotationScenes = schedule.value.rotationScenes.filter(scene => scene.id !== id)
}

const isCreateDisabled = computed(() => {
  if (!schedule.value.name.trim() || !schedule.value.type) return true

  if (schedule.value.type === 'single-time') {
    return !schedule.value.flowId ||
      !schedule.value.startTime ||
      !schedule.value.duration ||
      parseInt(schedule.value.duration) <= 0 ||
      !schedule.value.returnFlowId
  }

  if (schedule.value.type === 'recurring') {
    const needsCustom = schedule.value.repeatInterval === 'Custom interval'
    const customInvalid = needsCustom && (!schedule.value.customIntervalValue || parseInt(schedule.value.customIntervalValue) <= 0 || !schedule.value.customIntervalUnit)
    return !schedule.value.recurringFlowId ||
      !schedule.value.recurringStartTime ||
      !schedule.value.recurringEndTime ||
      !schedule.value.repeatInterval ||
      customInvalid ||
      !schedule.value.recurringDuration ||
      parseInt(schedule.value.recurringDuration) <= 0 ||
      !schedule.value.recurringReturnFlowId
  }

  if (schedule.value.type === 'multi-ad-block') {
    if (!schedule.value.multiStartTime || !schedule.value.multiReturnFlowId || !schedule.value.multiAds.length) return true
    const hasInvalidAd = schedule.value.multiAds.some(ad => !ad.flowId || !ad.duration || parseInt(ad.duration) <= 0)
    return hasInvalidAd
  }

  if (schedule.value.type === 'scene-rotation') {
    const needsCustom = schedule.value.rotationInterval === 'Custom interval'
    const customInvalid = needsCustom && (!schedule.value.rotationCustomValue || parseInt(schedule.value.rotationCustomValue) <= 0 || !schedule.value.rotationCustomUnit)
    const scenesInvalid = !schedule.value.rotationScenes.length || schedule.value.rotationScenes.some(scene => !scene.name.trim())
    return !schedule.value.rotationInterval ||
      customInvalid ||
      !schedule.value.rotationDuration ||
      parseInt(schedule.value.rotationDuration) <= 0 ||
      !schedule.value.rotationReturnFlowId ||
      scenesInvalid
  }

  if (schedule.value.type === 'time-slot-block') {
    const needsCustom = schedule.value.slotInterval === 'Custom interval'
    const customInvalid = needsCustom && (!schedule.value.slotCustomValue || parseInt(schedule.value.slotCustomValue) <= 0 || !schedule.value.slotCustomUnit)
    return !schedule.value.slotReturnFlowId ||
      !schedule.value.slotStartTime ||
      !schedule.value.slotEndTime ||
      !schedule.value.slotInterval ||
      customInvalid ||
      !schedule.value.slotDuration ||
      parseInt(schedule.value.slotDuration) <= 0
  }

  // Other types not yet implemented
  return false
})

const emit = defineEmits(['schedule-added', 'schedule-updated'])

const closeModal = () => {
  sidebar.value.closeSidebar()
  isEditMode.value = false
  editingScheduleId.value = null
}

const toggleSidebar = async () => {
  sidebar.value.toggleSidebar()
  // Fetch existing schedules when opening modal
  if (props.mediaflowId && isOpen.value) {
    try {
      const schedules = await MediaflowService.getAdSchedules(props.mediaflowId);
      existingSchedules.value = schedules || [];
    } catch (err) {
      console.error('Failed to fetch existing schedules', err);
      existingSchedules.value = [];
    }
  }
  modalAddAdScheduleSidebar.value.toggle();
}

// Helper function to convert time object or Date to seconds of day
const timeToSeconds = (timeObj) => {
  if (!timeObj) return null;

  // Handle Date objects
  if (timeObj instanceof Date) {
    return timeObj.getHours() * 3600 + timeObj.getMinutes() * 60 + timeObj.getSeconds();
  }

  // Handle time object { hours, minutes, seconds }
  if (typeof timeObj === 'object' && timeObj !== null && timeObj.hours !== undefined) {
    return (timeObj.hours || 0) * 3600 + (timeObj.minutes || 0) * 60 + (timeObj.seconds || 0);
  }

  return null;
}

// Helper function to parse interval to seconds
const parseIntervalToSeconds = (interval, customValue, customUnit) => {
  if (interval === 'Custom interval') {
    const value = parseInt(customValue) || 0;
    const unit = customUnit?.toLowerCase() || 'seconds';
    if (unit === 'minutes') return value * 60;
    if (unit === 'hours') return value * 3600;
    return value; // seconds
  }

  const match = interval?.match(/Every (\d+) (minute|hour|second)/i);
  if (match) {
    const value = parseInt(match[1]);
    const unit = match[2].toLowerCase();
    if (unit === 'minute') return value * 60;
    if (unit === 'hour') return value * 3600;
    return value; // seconds
  }

  return null;
}

// Get time ranges for a schedule (returns array of { start, end } in seconds of day)
// isExistingSchedule: true if scheduleData is from API (has UTC times), false if from form (has local times)
const getScheduleTimeRanges = (scheduleData, isExistingSchedule = false) => {
  const ranges = [];

  if (scheduleData.type === 'single-time') {
    let startTime = scheduleData.startTime;
    if (isExistingSchedule && typeof startTime === 'string') {
      // Convert UTC string to local time object
      startTime = convertUTCToLocal(startTime);
    }
    const startSeconds = timeToSeconds(startTime);
    if (startSeconds !== null) {
      const duration = parseInt(scheduleData.duration) || 0;
      ranges.push({ start: startSeconds, end: startSeconds + duration });
    }
  } else if (scheduleData.type === 'multi-ad-block') {
    let startTime = scheduleData.multiStartTime || scheduleData.startTime;
    if (isExistingSchedule && typeof startTime === 'string') {
      startTime = convertUTCToLocal(startTime);
    }
    const startSeconds = timeToSeconds(startTime);
    if (startSeconds !== null) {
      const totalDuration = scheduleData.ads?.reduce((sum, ad) => sum + (parseInt(ad.duration) || 0), 0) || 0;
      ranges.push({ start: startSeconds, end: startSeconds + totalDuration });
    }
  } else if (scheduleData.type === 'recurring') {
    let startTime = scheduleData.recurringStartTime || scheduleData.startTime;
    let endTime = scheduleData.recurringEndTime || scheduleData.endTime;
    if (isExistingSchedule) {
      if (typeof startTime === 'string') startTime = convertUTCToLocal(startTime);
      if (typeof endTime === 'string') endTime = convertUTCToLocal(endTime);
    }
    const startSeconds = timeToSeconds(startTime);
    const endSeconds = timeToSeconds(endTime);

    let intervalSeconds;
    if (isExistingSchedule && typeof scheduleData.repeatInterval === 'object') {
      // API format: { value: 10, unit: 'minutes' }
      const value = scheduleData.repeatInterval.value || 0;
      const unit = scheduleData.repeatInterval.unit?.toLowerCase() || 'seconds';
      if (unit === 'minutes') intervalSeconds = value * 60;
      else if (unit === 'hours') intervalSeconds = value * 3600;
      else intervalSeconds = value;
    } else {
      // Form format: "Every 5 minutes" or "Custom interval"
      intervalSeconds = parseIntervalToSeconds(
        scheduleData.repeatInterval,
        scheduleData.customIntervalValue,
        scheduleData.customIntervalUnit
      );
    }
    const duration = parseInt(scheduleData.recurringDuration || scheduleData.duration) || 0;

    if (startSeconds !== null && endSeconds !== null && intervalSeconds && intervalSeconds > 0) {
      // Generate occurrences within the time window
      for (let time = startSeconds; time < endSeconds; time += intervalSeconds) {
        ranges.push({ start: time, end: Math.min(time + duration, endSeconds) });
      }
    }
  } else if (scheduleData.type === 'scene-rotation') {
    let intervalSeconds;
    if (isExistingSchedule && typeof scheduleData.rotationInterval === 'object') {
      const value = scheduleData.rotationInterval.value || 0;
      const unit = scheduleData.rotationInterval.unit?.toLowerCase() || 'seconds';
      if (unit === 'minutes') intervalSeconds = value * 60;
      else if (unit === 'hours') intervalSeconds = value * 3600;
      else intervalSeconds = value;
    } else {
      intervalSeconds = parseIntervalToSeconds(
        scheduleData.rotationInterval,
        scheduleData.rotationCustomValue,
        scheduleData.rotationCustomUnit
      );
    }
    const duration = parseInt(scheduleData.rotationDuration || scheduleData.duration) || 0;

    if (intervalSeconds && intervalSeconds > 0) {
      // Generate occurrences for a full day
      for (let time = 0; time < 86400; time += intervalSeconds) {
        ranges.push({ start: time, end: time + duration });
      }
    }
  } else if (scheduleData.type === 'time-slot-block') {
    let startTime = scheduleData.slotStartTime || scheduleData.startTime;
    let endTime = scheduleData.slotEndTime || scheduleData.endTime;
    if (isExistingSchedule) {
      if (typeof startTime === 'string') startTime = convertUTCToLocal(startTime);
      if (typeof endTime === 'string') endTime = convertUTCToLocal(endTime);
    }
    const startSeconds = timeToSeconds(startTime);
    const endSeconds = timeToSeconds(endTime);

    let intervalSeconds;
    if (isExistingSchedule && typeof scheduleData.playInterval === 'object') {
      const value = scheduleData.playInterval.value || 0;
      const unit = scheduleData.playInterval.unit?.toLowerCase() || 'seconds';
      if (unit === 'minutes') intervalSeconds = value * 60;
      else if (unit === 'hours') intervalSeconds = value * 3600;
      else intervalSeconds = value;
    } else {
      intervalSeconds = parseIntervalToSeconds(
        scheduleData.slotInterval || scheduleData.playInterval,
        scheduleData.slotCustomValue,
        scheduleData.slotCustomUnit
      );
    }
    const duration = parseInt(scheduleData.slotDuration || scheduleData.duration) || 0;

    if (startSeconds !== null && endSeconds !== null && intervalSeconds && intervalSeconds > 0) {
      // Generate occurrences within the time window
      for (let time = startSeconds; time < endSeconds; time += intervalSeconds) {
        ranges.push({ start: time, end: Math.min(time + duration, endSeconds) });
      }
    }
  }

  return ranges;
}

// Check if two time ranges overlap
const rangesOverlap = (range1, range2) => {
  return range1.start < range2.end && range1.end > range2.start;
}

// Check for overlaps with existing schedules
const checkForOverlaps = () => {
  overlapWarning.value = null;

  if (!schedule.value.type) return;

  // Get time ranges for current schedule
  const currentRanges = getScheduleTimeRanges(schedule.value);
  if (currentRanges.length === 0) return;

  // Check against existing schedules (excluding the one being edited)
  const schedulesToCheck = existingSchedules.value.filter(s =>
    s.enabled && (!isEditMode.value || s._id !== editingScheduleId.value)
  );

  for (const existingSchedule of schedulesToCheck) {
    // Existing schedules from API have UTC times, so pass isExistingSchedule = true
    const existingRanges = getScheduleTimeRanges(existingSchedule, true);

    for (const currentRange of currentRanges) {
      for (const existingRange of existingRanges) {
        if (rangesOverlap(currentRange, existingRange)) {
          overlapWarning.value = `Warning: This schedule time overlaps with "${existingSchedule.name}"`;
          return;
        }
      }
    }
  }
}

// Helper function to convert interval object { value, unit } to human-readable format
const convertIntervalToHumanReadable = (interval) => {
  if (!interval) return null;

  // If it's already a string (human-readable), return it
  if (typeof interval === 'string') {
    return interval;
  }

  // If it's an object with value and unit
  if (typeof interval === 'object' && interval !== null && interval.value !== undefined && interval.unit !== undefined) {
    const value = interval.value;
    const unit = interval.unit.toLowerCase();

    // Normalize unit (handle both singular and plural)
    let normalizedUnit = unit;
    if (unit === 'second' || unit === 'seconds') {
      normalizedUnit = value === 1 ? 'second' : 'seconds';
    } else if (unit === 'minute' || unit === 'minutes') {
      normalizedUnit = value === 1 ? 'minute' : 'minutes';
    } else if (unit === 'hour' || unit === 'hours') {
      normalizedUnit = value === 1 ? 'hour' : 'hours';
    }

    // Format as "Every X unit(s)"
    const formatted = `Every ${value} ${normalizedUnit}`;

    // Check if it matches a predefined option
    if (repeatIntervalOptions.includes(formatted)) {
      return formatted;
    }

    // If it doesn't match, it's a custom interval
    return 'Custom interval';
  }

  return null;
}

const convertUTCToLocal = (utcTime) => {
  if (!utcTime) return null;

  let utcHours, utcMinutes, utcSeconds;
  let dateFromString = null;

  // Handle object format { hours, minutes, seconds } (assumed to be UTC)
  if (typeof utcTime === 'object' && utcTime !== null && utcTime.hours !== undefined) {
    utcHours = utcTime.hours;
    utcMinutes = utcTime.minutes !== undefined ? utcTime.minutes : 0;
    utcSeconds = utcTime.seconds !== undefined ? utcTime.seconds : 0;
  }
  else if (typeof utcTime === 'string' && utcTime.match(/^\d{2}:\d{2}:\d{2}$/)) {
    // Time-only string (HH:mm:ss)
    [utcHours, utcMinutes, utcSeconds] = utcTime.split(':').map(Number);
  }
  else if (utcTime instanceof Date) {
    // Already a Date object, return it directly (VueDatePicker with utc prop handles it)
    return utcTime;
  }
  else if (typeof utcTime === 'string') {
    // Check if it's a date-time string in format YYYY-MM-DDTHH:MM:SS (UTC from server)
    const dateTimeMatch = utcTime.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
    if (dateTimeMatch) {
      // Parse as UTC explicitly
      const [, year, month, day, hours, minutes, seconds] = dateTimeMatch.map(Number);
      const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, seconds || 0));
      // Return the Date object - it represents the UTC moment, but will be displayed in local timezone
      return utcDate;
    }
    // Try to parse as other date-time string formats
    dateFromString = new Date(utcTime);
    if (!isNaN(dateFromString.getTime())) {
      // If it has a timezone indicator (Z or +/-), it's already parsed correctly
      // If not, we need to check if it should be treated as UTC
      // For now, return it and let VueDatePicker handle it
      return dateFromString;
    } else {
      return null;
    }
  } else {
    return null;
  }

  // For time-only strings, create a Date object with today's date and converted local time
  const today = new Date();
  const utcDate = new Date(Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
    utcHours,
    utcMinutes,
    utcSeconds
  ));

  // Create a local Date object with today's date and the converted local time
  const localDate = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
    utcDate.getHours(),
    utcDate.getMinutes(),
    utcDate.getSeconds()
  );

  return localDate;
}

const convertLocalToUTC = (localTime) => {
  if (!localTime) return null;

  let date;
  if (typeof localTime === 'object' && localTime !== null && localTime.hours !== undefined) {
    // Handle time-only object { hours, minutes, seconds }
    const hours = localTime.hours;
    const minutes = localTime.minutes !== undefined ? localTime.minutes : 0;
    const seconds = localTime.seconds !== undefined ? localTime.seconds : 0;

    const today = new Date();
    date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, seconds);
  }
  else if (localTime instanceof Date) {
    // Date object from date-time picker
    date = localTime;
  }
  else if (typeof localTime === 'string') {
    if (localTime.match(/^\d{2}:\d{2}:\d{2}$/)) {
      // Time-only string (HH:mm:ss)
      const [hours, minutes, seconds] = localTime.split(':').map(Number);
      const today = new Date();
      date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, seconds);
    } else {
      // Date-time string
      date = new Date(localTime);
    }
  } else {
    return null;
  }

  if (isNaN(date.getTime())) return null;

  // Return date-time string in UTC format: YYYY-MM-DDTHH:MM:SS
  const year = date.getUTCFullYear();
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
  const day = date.getUTCDate().toString().padStart(2, '0');
  const hours = date.getUTCHours().toString().padStart(2, '0');
  const minutes = date.getUTCMinutes().toString().padStart(2, '0');
  const seconds = date.getUTCSeconds().toString().padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
}

const openForEdit = async (scheduleData) => {
  isEditMode.value = true
  editingScheduleId.value = scheduleData._id

  // Populate form with schedule data
  schedule.value.name = scheduleData.name || ''
  schedule.value.type = scheduleData.type || null

  // Populate fields based on type
  if (scheduleData.type === 'single-time') {
    schedule.value.flowId = scheduleData.flowId || null
    // Convert UTC time from server to Date object for VueDatePicker
    // VueDatePicker with utc prop will display this in local timezone
    schedule.value.startTime = convertUTCToLocal(scheduleData.startTime)
    schedule.value.duration = scheduleData.duration?.toString() || '30'
    schedule.value.returnFlowId = scheduleData.returnFlowId || null
  } else if (scheduleData.type === 'recurring') {
    schedule.value.recurringFlowId = scheduleData.flowId || null
    // Convert UTC times from server to local times for display
    schedule.value.recurringStartTime = convertUTCToLocal(scheduleData.startTime)
    schedule.value.recurringEndTime = convertUTCToLocal(scheduleData.endTime)

    // Convert interval object to human-readable format
    const convertedInterval = convertIntervalToHumanReadable(scheduleData.repeatInterval)
    schedule.value.repeatInterval = convertedInterval

    // If it's a custom interval, populate custom fields
    if (convertedInterval === 'Custom interval' && typeof scheduleData.repeatInterval === 'object' && scheduleData.repeatInterval !== null) {
      schedule.value.customIntervalValue = scheduleData.repeatInterval.value?.toString() || ''
      const unit = scheduleData.repeatInterval.unit?.toLowerCase() || ''
      // Convert API unit format to dropdown format
      if (unit === 'minute' || unit === 'minutes') {
        schedule.value.customIntervalUnit = 'Minutes'
      } else if (unit === 'second' || unit === 'seconds') {
        schedule.value.customIntervalUnit = 'Seconds'
      } else {
        schedule.value.customIntervalUnit = 'Seconds'
      }
    } else {
      schedule.value.customIntervalValue = ''
      schedule.value.customIntervalUnit = 'Seconds'
    }

    schedule.value.recurringDuration = scheduleData.duration?.toString() || '30'
    schedule.value.recurringReturnFlowId = scheduleData.returnFlowId || null
  } else if (scheduleData.type === 'multi-ad-block') {
    // Convert UTC time from server to local time for display
    schedule.value.multiStartTime = convertUTCToLocal(scheduleData.startTime)
    schedule.value.multiReturnFlowId = scheduleData.returnFlowId || null
    schedule.value.multiAds = scheduleData.ads?.map((ad, index) => ({
      id: Date.now() + index,
      flowId: ad.flowId || null,
      duration: ad.duration?.toString() || '30'
    })) || [{ id: Date.now(), flowId: null, duration: '30' }]
  } else if (scheduleData.type === 'scene-rotation') {
    // Convert interval object to human-readable format
    const convertedRotationInterval = convertIntervalToHumanReadable(scheduleData.rotationInterval)
    schedule.value.rotationInterval = convertedRotationInterval

    // If it's a custom interval, populate custom fields
    if (convertedRotationInterval === 'Custom interval' && typeof scheduleData.rotationInterval === 'object' && scheduleData.rotationInterval !== null) {
      schedule.value.rotationCustomValue = scheduleData.rotationInterval.value?.toString() || ''
      const unit = scheduleData.rotationInterval.unit?.toLowerCase() || ''
      // Convert API unit format to dropdown format
      if (unit === 'minute' || unit === 'minutes') {
        schedule.value.rotationCustomUnit = 'Minutes'
      } else if (unit === 'second' || unit === 'seconds') {
        schedule.value.rotationCustomUnit = 'Seconds'
      } else {
        schedule.value.rotationCustomUnit = 'Seconds'
      }
    } else {
      schedule.value.rotationCustomValue = ''
      schedule.value.rotationCustomUnit = 'Seconds'
    }

    schedule.value.rotationDuration = scheduleData.duration?.toString() || '30'
    schedule.value.rotationReturnFlowId = scheduleData.returnFlowId || null
    schedule.value.rotationScenes = scheduleData.scenes?.map((scene, index) => ({
      id: Date.now() + index,
      name: scene.name || ''
    })) || [{ id: Date.now() + 1, name: '' }]
  } else if (scheduleData.type === 'time-slot-block') {
    schedule.value.slotReturnFlowId = scheduleData.returnFlowId || null
    // Convert UTC times from server to local times for display
    schedule.value.slotStartTime = convertUTCToLocal(scheduleData.startTime)
    schedule.value.slotEndTime = convertUTCToLocal(scheduleData.endTime)

    // Convert interval object to human-readable format
    const convertedSlotInterval = convertIntervalToHumanReadable(scheduleData.playInterval)
    schedule.value.slotInterval = convertedSlotInterval

    // If it's a custom interval, populate custom fields
    if (convertedSlotInterval === 'Custom interval' && typeof scheduleData.playInterval === 'object' && scheduleData.playInterval !== null) {
      schedule.value.slotCustomValue = scheduleData.playInterval.value?.toString() || ''
      const unit = scheduleData.playInterval.unit?.toLowerCase() || ''
      // Convert API unit format to dropdown format
      if (unit === 'minute' || unit === 'minutes') {
        schedule.value.slotCustomUnit = 'Minutes'
      } else if (unit === 'second' || unit === 'seconds') {
        schedule.value.slotCustomUnit = 'Seconds'
      } else {
        schedule.value.slotCustomUnit = 'Seconds'
      }
    } else {
      schedule.value.slotCustomValue = ''
      schedule.value.slotCustomUnit = 'Seconds'
    }

    schedule.value.slotDuration = scheduleData.duration?.toString() || '30'
  }

  toggleSidebar()
}

const onReset = () => {
  schedule.value = {
    name: '',
    type: null,
    flowId: null,
    startTime: null,
    duration: '30',
    returnFlowId: null,
    recurringFlowId: null,
    recurringStartTime: null,
    recurringEndTime: null,
    repeatInterval: null,
    customIntervalValue: '',
    customIntervalUnit: 'Seconds',
    recurringDuration: '30',
    recurringReturnFlowId: null,
    multiStartTime: null,
    multiReturnFlowId: null,
    multiAds: [
      {
        id: Date.now(),
        flowId: null,
        duration: '30'
      }
    ],
    rotationInterval: null,
    rotationCustomValue: '',
    rotationCustomUnit: 'Seconds',
    rotationDuration: '30',
    rotationReturnFlowId: null,
    rotationScenes: [
      {
        id: Date.now() + 1,
        name: ''
      }
    ],
    slotReturnFlowId: null,
    slotStartTime: null,
    slotEndTime: null,
    slotInterval: null,
    slotCustomValue: '',
    slotCustomUnit: 'Seconds',
    slotDuration: '30'
  }
  error.value = null
}

const onScheduleTypeSelect = (type) => {
  schedule.value.type = type.value
  scheduleTypeDropdown.value?.hide();
}

const createSchedule = async () => {
  if (!schedule.value.name.trim()) {
    error.value = 'Schedule name is required'
    return
  }
  if (!schedule.value.type) {
    error.value = 'Schedule type is required'
    return
  }

  // Validate Single Time specific fields
  if (schedule.value.type === 'single-time') {
    if (!schedule.value.flowId) {
      error.value = 'Flow selection is required'
      return
    }
    if (!schedule.value.startTime) {
      error.value = 'Start time is required'
      return
    }
    if (!schedule.value.duration || parseInt(schedule.value.duration) <= 0) {
      error.value = 'Duration must be greater than 0'
      return
    }
    if (!schedule.value.returnFlowId) {
      error.value = 'Return flow is required'
      return
    }
  }

  // Validate Recurring fields
  if (schedule.value.type === 'recurring') {
    if (!schedule.value.recurringFlowId) {
      error.value = 'Flow selection is required'
      return
    }
    if (!schedule.value.repeatInterval) {
      error.value = 'Repeat interval is required'
      return
    }
    if (schedule.value.repeatInterval === 'Custom interval') {
      if (!schedule.value.customIntervalValue || parseInt(schedule.value.customIntervalValue) <= 0) {
        error.value = 'Custom interval must be greater than 0'
        return
      }
      if (!schedule.value.customIntervalUnit) {
        error.value = 'Custom interval unit is required'
        return
      }
    }
    if (!schedule.value.recurringDuration || parseInt(schedule.value.recurringDuration) <= 0) {
      error.value = 'Duration must be greater than 0'
      return
    }
    if (!schedule.value.recurringReturnFlowId) {
      error.value = 'Return flow is required'
      return
    }
  }

  // Validate Multi Ad Block fields
  if (schedule.value.type === 'multi-ad-block') {
    if (!schedule.value.multiStartTime) {
      error.value = 'Start time is required'
      return
    }
    if (!schedule.value.multiReturnFlowId) {
      error.value = 'Return flow is required'
      return
    }
    if (!schedule.value.multiAds.length) {
      error.value = 'At least one ad is required'
      return
    }
    const hasInvalidAd = schedule.value.multiAds.some(ad => !ad.flowId || !ad.duration || parseInt(ad.duration) <= 0)
    if (hasInvalidAd) {
      error.value = 'All ads must have a flow and duration'
      return
    }
  }

  // Validate Scene Rotation fields
  if (schedule.value.type === 'scene-rotation') {
    if (!schedule.value.rotationInterval) {
      error.value = 'Rotation interval is required'
      return
    }
    if (schedule.value.rotationInterval === 'Custom interval') {
      if (!schedule.value.rotationCustomValue || parseInt(schedule.value.rotationCustomValue) <= 0) {
        error.value = 'Custom interval must be greater than 0'
        return
      }
      if (!schedule.value.rotationCustomUnit) {
        error.value = 'Custom interval unit is required'
        return
      }
    }
    if (!schedule.value.rotationDuration || parseInt(schedule.value.rotationDuration) <= 0) {
      error.value = 'Duration must be greater than 0'
      return
    }
    if (!schedule.value.rotationReturnFlowId) {
      error.value = 'Return flow is required'
      return
    }
    if (!schedule.value.rotationScenes.length || schedule.value.rotationScenes.some(scene => !scene.name.trim())) {
      error.value = 'All scenes must have a name'
      return
    }
  }

  // Validate Time Slot Block fields
  if (schedule.value.type === 'time-slot-block') {
    if (!schedule.value.slotReturnFlowId) {
      error.value = 'Return flow is required'
      return
    }
    if (!schedule.value.slotStartTime) {
      error.value = 'Start time is required'
      return
    }
    if (!schedule.value.slotEndTime) {
      error.value = 'End time is required'
      return
    }
    if (!schedule.value.slotInterval) {
      error.value = 'Play interval is required'
      return
    }
    if (schedule.value.slotInterval === 'Custom interval') {
      if (!schedule.value.slotCustomValue || parseInt(schedule.value.slotCustomValue) <= 0) {
        error.value = 'Custom interval must be greater than 0'
        return
      }
      if (!schedule.value.slotCustomUnit) {
        error.value = 'Custom interval unit is required'
        return
      }
    }
    if (!schedule.value.slotDuration || parseInt(schedule.value.slotDuration) <= 0) {
      error.value = 'Duration must be greater than 0'
      return
    }
  }

  try {
    error.value = null

    // Transform form data to API format
    let apiData = {
      name: schedule.value.name.trim(),
      type: schedule.value.type
    }

    if (schedule.value.type === 'single-time') {
      apiData.flowId = schedule.value.flowId
      // Convert local time to UTC time string for server
      apiData.startTime = convertLocalToUTC(schedule.value.startTime)
      apiData.duration = parseInt(schedule.value.duration)
      apiData.returnFlowId = schedule.value.returnFlowId
    } else if (schedule.value.type === 'recurring') {
      apiData.flowId = schedule.value.recurringFlowId
      // Convert local times to UTC time strings for server
      apiData.startTime = convertLocalToUTC(schedule.value.recurringStartTime)
      apiData.endTime = convertLocalToUTC(schedule.value.recurringEndTime)
      apiData.returnFlowId = schedule.value.recurringReturnFlowId
      apiData.duration = parseInt(schedule.value.recurringDuration)

      if (schedule.value.repeatInterval === 'Custom interval') {
        apiData.repeatInterval = {
          value: parseInt(schedule.value.customIntervalValue),
          unit: schedule.value.customIntervalUnit.toLowerCase()
        }
      } else {
        // Parse interval like "Every 5 minutes" to { value: 5, unit: 'minutes' }
        const intervalMatch = schedule.value.repeatInterval?.match(/Every (\d+) (minute|hour|second)/i)
        if (intervalMatch) {
          apiData.repeatInterval = {
            value: parseInt(intervalMatch[1]),
            unit: intervalMatch[2].toLowerCase() + (intervalMatch[2].toLowerCase() === 'minute' ? 's' : '')
          }
        } else {
          apiData.repeatInterval = schedule.value.repeatInterval
        }
      }
    } else if (schedule.value.type === 'multi-ad-block') {
      // Convert local time to UTC time string for server
      apiData.startTime = convertLocalToUTC(schedule.value.multiStartTime)
      apiData.returnFlowId = schedule.value.multiReturnFlowId
      apiData.ads = schedule.value.multiAds.map(ad => ({
        flowId: ad.flowId,
        duration: parseInt(ad.duration)
      }))
    } else if (schedule.value.type === 'scene-rotation') {
      apiData.returnFlowId = schedule.value.rotationReturnFlowId
      apiData.duration = parseInt(schedule.value.rotationDuration)

      if (schedule.value.rotationInterval === 'Custom interval') {
        apiData.rotationInterval = {
          value: parseInt(schedule.value.rotationCustomValue),
          unit: schedule.value.rotationCustomUnit.toLowerCase()
        }
      } else {
        const intervalMatch = schedule.value.rotationInterval?.match(/Every (\d+) (minute|hour|second)/i)
        if (intervalMatch) {
          apiData.rotationInterval = {
            value: parseInt(intervalMatch[1]),
            unit: intervalMatch[2].toLowerCase() + (intervalMatch[2].toLowerCase() === 'minute' ? 's' : '')
          }
        } else {
          apiData.rotationInterval = schedule.value.rotationInterval
        }
      }

      apiData.scenes = schedule.value.rotationScenes.map(scene => ({
        name: scene.name.trim()
      }))
    } else if (schedule.value.type === 'time-slot-block') {
      apiData.returnFlowId = schedule.value.slotReturnFlowId
      // Convert local times to UTC time strings for server
      apiData.startTime = convertLocalToUTC(schedule.value.slotStartTime)
      apiData.endTime = convertLocalToUTC(schedule.value.slotEndTime)
      apiData.duration = parseInt(schedule.value.slotDuration)

      if (schedule.value.slotInterval === 'Custom interval') {
        apiData.playInterval = {
          value: parseInt(schedule.value.slotCustomValue),
          unit: schedule.value.slotCustomUnit.toLowerCase()
        }
      } else {
        const intervalMatch = schedule.value.slotInterval?.match(/Every (\d+) (minute|hour|second)/i)
        if (intervalMatch) {
          apiData.playInterval = {
            value: parseInt(intervalMatch[1]),
            unit: intervalMatch[2].toLowerCase() + (intervalMatch[2].toLowerCase() === 'minute' ? 's' : '')
          }
        } else {
          apiData.playInterval = schedule.value.slotInterval
        }
      }
    }

    if (isEditMode.value && editingScheduleId.value) {
      // Update existing schedule
      await MediaflowService.updateAdSchedule(props.mediaflowId, editingScheduleId.value, apiData)
      notify({ group: 'success', text: 'Ad Schedule Updated' })
      emit('schedule-updated')
    } else {
      // Create new schedule
      await MediaflowService.createAdSchedule(props.mediaflowId, apiData)
      notify({ group: 'success', text: 'Ad Schedule Created' })
      emit('schedule-added')
    }

    closeModal()
  } catch (err) {
    console.error('Failed to save schedule', err)
    error.value = err?.message || 'Failed to save schedule'
    notify({ group: 'error', text: err?.message || 'Failed to save schedule' })
  }
}

const onSidebarClosed = () => {
  onReset()
  isEditMode.value = false
  editingScheduleId.value = null
  overlapWarning.value = null
}

// Watch for changes that could cause overlaps
watch(() => [
  schedule.value.type,
  schedule.value.startTime,
  schedule.value.duration,
  schedule.value.multiStartTime,
  schedule.value.multiAds,
  schedule.value.recurringStartTime,
  schedule.value.recurringEndTime,
  schedule.value.repeatInterval,
  schedule.value.customIntervalValue,
  schedule.value.customIntervalUnit,
  schedule.value.recurringDuration,
  schedule.value.rotationInterval,
  schedule.value.rotationCustomValue,
  schedule.value.rotationCustomUnit,
  schedule.value.rotationDuration,
  schedule.value.slotStartTime,
  schedule.value.slotEndTime,
  schedule.value.slotInterval,
  schedule.value.slotCustomValue,
  schedule.value.slotCustomUnit,
  schedule.value.slotDuration
], () => {
  // Debounce overlap check
  setTimeout(() => {
    checkForOverlaps();
  }, 300);
}, { deep: true });

defineExpose({ toggleSidebar, openForEdit })
</script>

<template>
  <Sidebar ref="sidebar" :stage="1" @closed="onReset" width="md:w-[420px]"
    stageOneCustomClass="lg:h-[80vh] !overflow-y-auto">
    <template v-slot:stage-one>
      <h3 class="text-lg font-medium mb-2 mt-3 !px-4">
        {{ isEditMode ? 'Edit Schedule' : 'Create New Schedule' }}
      </h3>
      <p class="text-surface-8 text-sm mb-4 !px-4">
        {{ isEditMode ? 'Update your ad schedule settings below.' : 'Choose a schedule type and flow to get started.'
        }}
      </p>
      <div v-if="error" class="text-red-600 text-sm mb-2 mt-4">{{ error }}</div>
      <Alert v-if="overlapWarning" type="error" :text="overlapWarning" class="flex items-center p-2 mb-3" />
      <div class="flex-1 min-h-0">
        <div class="px-3 mb-3">
          <FormInput label="Schedule name" v-model="schedule.name" placeholder="Enter schedule name" />
        </div>
        <div class="px-3 mb-3">
          <label class="block text-sm text-dark-9  mb-2">
            Schedule type
          </label>
          <drop-down-menu ref="scheduleTypeDropdown" type="flat" size="md" fullWidth>
            <template #toggle-button>
              {{schedule.type ? scheduleTypes.find(t => t.value === schedule.type)?.label : 'Select schedule type'}}
              <icon-base class="ml-auto">
                <icon-chevron-down />
              </icon-base>
            </template>

            <template #menu-items>
              <drop-down-menu-item
                customClasses="max-w-full w-full h-12 flex flex-col text-wrap text-left items-start px-3 py-2 text-tiny hover:bg-surface-4"
                v-for="type in scheduleTypes" :key="type.value" @click="onScheduleTypeSelect(type)">
                <div class="flex items-center">
                  <component :is="type.icon" class="mr-2" />
                  <div class="flex flex-col items-start">
                    <span class="text-sm font-medium">{{ type.label }}</span>
                    <span class="text-xs text-surface-6">{{ type.description }}</span>
                  </div>
                </div>
              </drop-down-menu-item>
            </template>
          </drop-down-menu>
        </div>

        <!-- Single Time Schedule Configuration -->
        <div v-if="schedule.type === 'single-time'" class="px-3 mb-3">
          <div class="mb-4">
            <h4 class="text-base font-medium text-white mb-1">Single Time</h4>
            <p class="text-sm text-surface-6">
              Schedule a specific ad scene to play at a fixed time once.
            </p>
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Flow selection
            </label>
            <drop-down-menu type="flat" fullWidth ref="flowDropdown">
              <template #toggle-button>
                <span v-if="schedule.flowId">
                  {{mediaflow?.flows?.find(f => f._id === schedule.flowId)?.name || 'Select flow'}}
                </span>
                <span v-else class="text-surface-6">Select flow</span>
                <icon-base class="ml-auto">
                  <icon-chevron-down />
                </icon-base>
              </template>

              <template #menu-items>
                <drop-down-menu-item v-for="flow in mediaflow?.flows || []" :key="flow._id"
                  @click="schedule.flowId = flow._id; flowDropdown?.hide()">
                  <span class="ml-2">{{ flow.name }}</span>
                </drop-down-menu-item>
              </template>
            </drop-down-menu>
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Start Date & Time
            </label>
            <div class="flex items-center relative">
              <IconBase class="absolute z-10 !left-2 text-surface-8">
                <icon-calendar />
              </IconBase>
              <IconBase class="absolute z-10 !left-7 text-surface-8">
                <icon-clock />
              </IconBase>
              <VueDatePicker text-input enable-seconds format="yyyy-MM-dd HH:mm:ss" v-model="schedule.startTime"
                :min-date="minDate" placeholder="Start date and time"
                inputClassName="bg-surface-3 hover:bg-surface-4 rounded-md border-none text-white font-normal w-full pl-14 font-inter text-tiny" />
            </div>
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Duration (seconds)
            </label>
            <FormInput v-model="schedule.duration" type="number" placeholder="Enter duration in seconds" min="0" />
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Return flow
            </label>
            <drop-down-menu type="flat" fullWidth ref="singleReturnFlowDropdown">
              <template #toggle-button>
                <span v-if="schedule.returnFlowId">
                  {{mediaflow?.flows?.find(f => f._id === schedule.returnFlowId)?.name || 'Select return flow'}}
                </span>
                <span v-else class="text-surface-6">Select return flow</span>
                <icon-base class="ml-auto">
                  <icon-chevron-down />
                </icon-base>
              </template>

              <template #menu-items>
                <drop-down-menu-item v-for="flow in mediaflow?.flows || []" :key="flow._id"
                  @click="schedule.returnFlowId = flow._id; singleReturnFlowDropdown?.hide()">
                  <span class="ml-2">{{ flow.name }}</span>
                </drop-down-menu-item>
              </template>
            </drop-down-menu>
          </div>
        </div>

        <!-- Recurring Schedule Configuration -->
        <div v-if="schedule.type === 'recurring'" class="px-3 mb-3">
          <div class="mb-4">
            <div class="flex justify-between items-start">
              <div>
                <h4 class="text-base font-medium text-white mb-1">Recurring Trigger</h4>
                <p class="text-sm text-surface-6">
                  Schedule a specific ad scene to play repeatedly at regular intervals.
                </p>
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Flow selection
            </label>
            <drop-down-menu type="flat" fullWidth ref="rotationReturnFlowDropdown">
              <template #toggle-button>
                <span v-if="schedule.recurringFlowId">
                  {{mediaflow?.flows?.find(f => f._id === schedule.recurringFlowId)?.name || 'Select flow'}}
                </span>
                <span v-else class="text-surface-6">Select flow</span>
                <icon-base class="ml-auto">
                  <icon-chevron-down />
                </icon-base>
              </template>

              <template #menu-items>
                <drop-down-menu-item v-for="flow in mediaflow?.flows || []" :key="flow._id"
                  @click="schedule.recurringFlowId = flow._id; rotationReturnFlowDropdown?.hide()">
                  <span class="ml-2">{{ flow.name }}</span>
                </drop-down-menu-item>
              </template>
            </drop-down-menu>
          </div>

          <div class="mb-3 grid grid-cols-1 gap-3">
            <div>
              <label class="block text-sm text-dark-9 mb-2">
                Start date & time
              </label>
              <div class="flex items-center relative">
                <IconBase class="absolute z-10 !left-2 text-surface-8">
                  <icon-calendar />
                </IconBase>
                <IconBase class="absolute z-10 !left-7 text-surface-8">
                  <icon-clock />
                </IconBase>
                <VueDatePicker text-input enable-seconds format="yyyy-MM-dd HH:mm:ss"
                  v-model="schedule.recurringStartTime" :min-date="minDate" placeholder="Start date and time"
                  inputClassName="bg-surface-3 hover:bg-surface-4 rounded-md border-none text-white font-normal w-full pl-14 font-inter text-tiny" />
              </div>
            </div>
            <div>
              <label class="block text-sm text-dark-9 mb-2">
                End date & time
              </label>
              <div class="flex items-center relative">
                <IconBase class="absolute z-10 !left-2 text-surface-8">
                  <icon-calendar />
                </IconBase>
                <IconBase class="absolute z-10 !left-7 text-surface-8">
                  <icon-clock />
                </IconBase>
                <VueDatePicker text-input enable-seconds format="yyyy-MM-dd HH:mm:ss"
                  v-model="schedule.recurringEndTime" :min-date="minDate" placeholder="Start date and time"
                  inputClassName="bg-surface-3 hover:bg-surface-4 rounded-md border-none text-white font-normal w-full pl-14 font-inter text-tiny" />
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Repeat interval
            </label>
            <drop-down-menu type="flat" fullWidth ref="repeatIntervalDropdown">
              <template #toggle-button>
                <span v-if="schedule.repeatInterval">
                  {{ schedule.repeatInterval }}
                </span>
                <span v-else class="text-surface-6">Select interval</span>
                <icon-base class="ml-auto">
                  <icon-chevron-down />
                </icon-base>
              </template>

              <template #menu-items>
                <drop-down-menu-item v-for="option in repeatIntervalOptions" :key="option"
                  @click="schedule.repeatInterval = option; repeatIntervalDropdown?.hide()">
                  <span class="ml-2">{{ option }}</span>
                </drop-down-menu-item>
              </template>
            </drop-down-menu>
          </div>

          <div v-if="schedule.repeatInterval === 'Custom interval'" class="mb-3 flex items-center gap-3">
            <div class="flex-1">
              <label class="block text-sm text-dark-9 mb-2">
                Custom value
              </label>
              <FormInput v-model="schedule.customIntervalValue" type="number" placeholder="Enter interval time"
                min="1" />
            </div>
            <div class="w-36">
              <label class="block text-sm text-dark-9 mb-2">
                Unit
              </label>
              <drop-down-menu type="flat" fullWidth ref="customIntervalUnitDropdown">
                <template #toggle-button>
                  <span>{{ schedule.customIntervalUnit }}</span>
                  <icon-base class="ml-auto">
                    <icon-chevron-down />
                  </icon-base>
                </template>

                <template #menu-items>
                  <drop-down-menu-item
                    @click="schedule.customIntervalUnit = 'Seconds'; customIntervalUnitDropdown?.hide()">
                    <span class="ml-2">Seconds</span>
                  </drop-down-menu-item>
                  <drop-down-menu-item
                    @click="schedule.customIntervalUnit = 'Minutes'; customIntervalUnitDropdown?.hide()">
                    <span class="ml-2">Minutes</span>
                  </drop-down-menu-item>
                </template>
              </drop-down-menu>
            </div>
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Duration (seconds)
            </label>
            <FormInput v-model="schedule.recurringDuration" type="number" placeholder="Enter duration in seconds"
              min="0" />
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Return flow
            </label>
            <drop-down-menu type="flat" fullWidth ref="recurringReturnFlowDropdown">
              <template #toggle-button>
                <span v-if="schedule.recurringReturnFlowId">
                  {{mediaflow?.flows?.find(f => f._id === schedule.recurringReturnFlowId)?.name || 'Select return flow'
                  }}
                </span>
                <span v-else class="text-surface-6">Select return flow</span>
                <icon-base class="ml-auto">
                  <icon-chevron-down />
                </icon-base>
              </template>

              <template #menu-items>
                <drop-down-menu-item v-for="flow in mediaflow?.flows || []" :key="flow._id"
                  @click="schedule.recurringReturnFlowId = flow._id; recurringReturnFlowDropdown?.hide()">
                  <span class="ml-2">{{ flow.name }}</span>
                </drop-down-menu-item>
              </template>
            </drop-down-menu>
          </div>
        </div>

        <!-- Scene Rotation Configuration -->
        <div v-if="schedule.type === 'scene-rotation'" class="px-3 mb-3">
          <div class="mb-4 flex justify-between items-start">
            <div>
              <h4 class="text-base font-medium text-white mb-1">Scene Rotation</h4>
              <p class="text-sm text-surface-6">
                Rotate through a list of ad scenes on a fixed interval, cycling through each scene in order.
              </p>
            </div>
            <div class="text-right">
              <p class="text-xs text-surface-6 mb-2">Scene List</p>
              <FormButton type="primary" size="sm" @click="addRotationScene" class="flex items-center">
                <icon-base width="12" height="12" width-view-box="14" height-view-box="14" class="mr-1">
                  <icon-plus />
                </icon-base>
                Add Scene
              </FormButton>
            </div>
          </div>

          <div class="mb-2">
            <p class="text-xs text-surface-6">
              Scenes will play in the order listed above, returning to the first scene after the last one.
            </p>
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Rotation interval
            </label>
            <drop-down-menu type="flat" size="md" class="w-full"
              customClasses="flex items-center justify-between font-normal"
              listClasses="dropdown-menu_md w-full">
              <template #toggle-button>
                <span v-if="schedule.rotationInterval">
                  {{ schedule.rotationInterval }}
                </span>
                <span v-else class="text-surface-6">Select interval</span>
              </template>

              <template #menu-items>
                <drop-down-menu-item v-for="option in repeatIntervalOptions" :key="option"
                  @click="schedule.rotationInterval = option">
                  <span class="ml-2">{{ option }}</span>
                </drop-down-menu-item>
              </template>
            </drop-down-menu>
          </div>

          <div v-if="schedule.rotationInterval === 'Custom interval'" class="mb-3 flex items-center gap-3">
            <div class="flex-1">
              <label class="block text-sm text-dark-9 mb-2">
                Custom value
              </label>
              <FormInput v-model="schedule.rotationCustomValue" type="number" placeholder="Enter interval time"
                min="1" />
            </div>
            <div class="w-36">
              <label class="block text-sm text-dark-9 mb-2">
                Unit
              </label>
              <drop-down-menu type="flat" size="md" class="w-full"
                customClasses="flex items-center justify-between font-normal"
                listClasses="dropdown-menu_md w-full">
                <template #toggle-button>
                  <span>{{ schedule.rotationCustomUnit }}</span>
                </template>

                <template #menu-items>
                  <drop-down-menu-item @click="schedule.rotationCustomUnit = 'Seconds'">
                    <span class="ml-2">Seconds</span>
                  </drop-down-menu-item>
                  <drop-down-menu-item @click="schedule.rotationCustomUnit = 'Minutes'">
                    <span class="ml-2">Minutes</span>
                  </drop-down-menu-item>
                </template>
              </drop-down-menu>
            </div>
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Duration (seconds)
            </label>
            <FormInput v-model="schedule.rotationDuration" type="number" placeholder="Enter duration in seconds"
              min="0" />
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Return flow
            </label>
            <drop-down-menu type="flat" size="md" class="w-full"
              customClasses="flex items-center justify-between font-normal"
              listClasses="dropdown-menu_md w-full flow-dropdown-menu">
              <template #toggle-button>
                <span v-if="schedule.rotationReturnFlowId">
                  {{mediaflow?.flows?.find(f => f._id === schedule.rotationReturnFlowId)?.name || 'Select return flow'
                  }}
                </span>
                <span v-else class="text-surface-6">Select return flow</span>
              </template>

              <template #menu-items>
                <drop-down-menu-item v-for="flow in mediaflow?.flows || []" :key="flow._id"
                  @click="schedule.rotationReturnFlowId = flow._id">
                  <span class="ml-2">{{ flow.name }}</span>
                </drop-down-menu-item>
              </template>
            </drop-down-menu>
          </div>

          <div class="space-y-2">
            <div v-for="(scene, idx) in schedule.rotationScenes" :key="scene.id"
              class="border border-surface-3 rounded-md p-3 relative">
              <div class="flex justify-between items-center mb-2">
                <h5 class="text-sm font-medium text-white">Scene {{ idx + 1 }}</h5>
                <FormButton v-if="schedule.rotationScenes.length > 1" isIcon type="danger" size="sm"
                  @click="removeRotationScene(scene.id)">
                  <IconBase class="mx-auto">
                    <IconBin />
                  </IconBase>
                </FormButton>
              </div>
              <FormInput v-model="scene.name" placeholder="Enter scene name" />
            </div>
          </div>
        </div>

        <!-- Time Slot Block Configuration -->
        <div v-if="schedule.type === 'time-slot-block'" class="px-3 mb-3">
          <div class="mb-4">
            <h4 class="text-base font-medium text-white mb-1">Time Slot Block</h4>
            <p class="text-sm text-surface-6">
              Show ads at fixed intervals within a specific time window throughout the day.
            </p>
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Return flow
            </label>
            <drop-down-menu type="flat" fullWidth ref="slotReturnFlowDropdown">
              <template #toggle-button>
                <span v-if="schedule.slotReturnFlowId">
                  {{mediaflow?.flows?.find(f => f._id === schedule.slotReturnFlowId)?.name || 'Select return flow'}}
                </span>
                <span v-else class="text-surface-6">Select return flow</span>
                <icon-base class="ml-auto">
                  <icon-chevron-down />
                </icon-base>
              </template>

              <template #menu-items>
                <drop-down-menu-item v-for="flow in mediaflow?.flows || []" :key="flow._id"
                  @click="schedule.slotReturnFlowId = flow._id">
                  <span class="ml-2">{{ flow.name }}</span>
                </drop-down-menu-item>
              </template>
            </drop-down-menu>
          </div>

          <div class="mb-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label class="block text-sm text-dark-9 mb-2">
                Start date & time
              </label>
              <div class="flex items-center relative">
                <IconBase class="absolute z-10 !left-2 text-surface-8">
                  <icon-calendar />
                </IconBase>
                <IconBase class="absolute z-10 !left-7 text-surface-8">
                  <icon-clock />
                </IconBase>
                <VueDatePicker text-input enable-seconds format="yyyy-MM-dd HH:mm:ss"
                  v-model="schedule.slotStartTime" :min-date="minDate" placeholder="Start date and time"
                  inputClassName="bg-surface-3 hover:bg-surface-4 rounded-md border-none text-white font-normal w-full pl-14 font-inter text-tiny" />
              </div>
            </div>
            <div>
              <label class="block text-sm text-dark-9 mb-2">
                End date & time
              </label>
              <div class="flex items-center relative">
                <IconBase class="absolute z-10 !left-2 text-surface-8">
                  <icon-calendar />
                </IconBase>
                <IconBase class="absolute z-10 !left-7 text-surface-8">
                  <icon-clock />
                </IconBase>
                <VueDatePicker text-input enable-seconds format="yyyy-MM-dd HH:mm:ss"
                  v-model="schedule.slotEndTime" :min-date="minDate" placeholder="End date and time"
                  inputClassName="bg-surface-3 hover:bg-surface-4 rounded-md border-none text-white font-normal w-full pl-14 font-inter text-tiny" />
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Play interval
            </label>
            <drop-down-menu type="flat" fullWidth ref="slotIntervalDropdown">
              <template #toggle-button>
                <span v-if="schedule.slotInterval">
                  {{ schedule.slotInterval }}
                </span>
                <span v-else class="text-surface-6">Select interval</span>
                <icon-base class="ml-auto">
                  <icon-chevron-down />
                </icon-base>
              </template>

              <template #menu-items>
                <drop-down-menu-item v-for="option in repeatIntervalOptions" :key="option"
                  @click="schedule.slotInterval = option; slotIntervalDropdown.value?.hide()">
                  <span class="ml-2">{{ option }}</span>
                </drop-down-menu-item>
              </template>
            </drop-down-menu>
          </div>

          <div v-if="schedule.slotInterval === 'Custom interval'" class="mb-3 flex items-center gap-3">
            <div class="flex-1">
              <label class="block text-sm text-dark-9 mb-2">
                Custom value
              </label>
              <FormInput v-model="schedule.slotCustomValue" type="number" placeholder="Enter interval time" min="1" />
            </div>
            <div class="w-36">
              <label class="block text-sm text-dark-9 mb-2">
                Unit
              </label>
              <drop-down-menu type="flat" fullWidth ref="slotCustomUnitDropdown">
                <template #toggle-button>
                  <span>{{ schedule.slotCustomUnit }}</span>
                </template>

                <template #menu-items>
                  <drop-down-menu-item
                    @click="schedule.slotCustomUnit = 'Seconds'; slotCustomUnitDropdown.value?.hide()">
                    <span class="ml-2">Seconds</span>
                  </drop-down-menu-item>
                  <drop-down-menu-item
                    @click="schedule.slotCustomUnit = 'Minutes'; slotCustomUnitDropdown.value?.hide()">
                    <span class="ml-2">Minutes</span>
                  </drop-down-menu-item>
                </template>
              </drop-down-menu>
            </div>
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Duration (seconds)
            </label>
            <FormInput v-model="schedule.slotDuration" type="number" placeholder="Enter duration in seconds" min="0" />
          </div>
        </div>

        <!-- Multi Ad Block Configuration -->
        <div v-if="schedule.type === 'multi-ad-block'" class="px-3 mb-3">
          <div class="mb-1 flex justify-between items-start">
            <h4 class="text-base font-medium text-white mb-1">Multi Ad Block</h4>
            <!-- <FormButton type="primary" size="sm" @click="addMultiAd" class="flex items-center">
                  <icon-base width="12" height="12" width-view-box="14" height-view-box="14" class="mr-2">
                    <icon-plus />
                  </icon-base>
                  Add Ad
                </FormButton> -->
          </div>

          <p class="text-sm text-surface-6 mb-3">
            Schedule a sequence of multiple ad scenes to play back-to-back at a specific time.
          </p>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Start Date & Time
            </label>
            <div class="flex items-center relative">
              <IconBase class="absolute z-10 !left-2 text-surface-8">
                <icon-calendar />
              </IconBase>
              <IconBase class="absolute z-10 !left-7 text-surface-8">
                <icon-clock />
              </IconBase>
              <VueDatePicker text-input enable-seconds format="yyyy-MM-dd HH:mm:ss" v-model="schedule.multiStartTime"
                :min-date="minDate"
                inputClassName="bg-surface-3 hover:bg-surface-4 rounded-md border-none text-white font-normal w-full pl-14" />
            </div>
          </div>

          <div class="mb-3">
            <label class="block text-sm text-dark-9 mb-2">
              Return flow
            </label>
            <drop-down-menu type="flat" fullWidth ref="multiReturnFlowDropdown">
              <template #toggle-button>
                <span v-if="schedule.multiReturnFlowId">
                  {{mediaflow?.flows?.find(f => f._id === schedule.multiReturnFlowId)?.name || 'Select return flow'}}
                </span>
                <span v-else class="text-surface-6">Select return flow</span>
                <icon-base class="ml-auto">
                  <icon-chevron-down />
                </icon-base>
              </template>

              <template #menu-items>
                <drop-down-menu-item v-for="flow in mediaflow?.flows || []" :key="flow._id"
                  @click="schedule.multiReturnFlowId = flow._id; multiReturnFlowDropdown?.hide()">
                  <span class="ml-2">{{ flow.name }}</span>
                </drop-down-menu-item>
              </template>
            </drop-down-menu>
          </div>

          <div class="mb-3 items-center flex justify-between items-start">
            <h4 class="text-sm font-medium text-white mb-1">Ad Sequence</h4>
            <FormButton type="primary" size="sm" @click="addMultiAd" class="flex items-center">
              <icon-base width="12" height="12" width-view-box="14" height-view-box="14" class="mr-2">
                <icon-plus />
              </icon-base>
              Add Ad
            </FormButton>
          </div>

          <div class="space-y-3">
            <div v-for="(ad, idx) in schedule.multiAds" :key="ad.id"
              class="border border-surface-3 rounded-md p-3 relative">
              <div class="flex justify-between items-center mb-2">
                <h5 class="text-sm font-medium text-white">Ad {{ idx + 1 }}</h5>
                <FormButton v-if="schedule.multiAds.length > 1" isIcon type="danger" size="sm"
                  @click="removeMultiAd(ad.id)">
                  <IconBase class="mx-auto">
                    <IconBin />
                  </IconBase>
                </FormButton>
              </div>

              <div class="mb-3">
                <label class="block text-sm text-dark-9 mb-2">
                  Flow
                </label>
                <drop-down-menu type="flat" fullWidth ref="adFlowDropdown">
                  <template #toggle-button>
                    <span v-if="ad.flowId">
                      {{mediaflow?.flows?.find(f => f._id === ad.flowId)?.name || 'Select flow'}}
                    </span>
                    <span v-else class="text-surface-6">Select flow</span>
                    <icon-base class="ml-auto">
                      <icon-chevron-down />
                    </icon-base>
                  </template>

                  <template #menu-items>
                    <drop-down-menu-item v-for="flow in mediaflow?.flows || []" :key="flow._id"
                      @click="ad.flowId = flow._id; adFlowDropdown?.hide()">
                      <span class="ml-2">{{ flow.name }}</span>
                    </drop-down-menu-item>
                  </template>
                </drop-down-menu>
              </div>

              <div class="mb-1">
                <label class="block text-sm text-dark-9 mb-2">
                  Duration (seconds)
                </label>
                <FormInput v-model="ad.duration" type="number" placeholder="Enter duration in seconds" min="0" />
              </div>
            </div>
          </div>
        </div>
        <div class="px-3 flex-shrink-0 pb-4">
          <div class="text-center">
            <FormButton :disabled="isCreateDisabled" @click="createSchedule" type="primary" size="md"
              class="w-full mt-4 justify-center">
              {{ isEditMode ? 'Update' : 'Create' }}
            </FormButton>
            <FormButton type="secondary" size="md" class="w-full mb-3 mb:md-0 mt-2 justify-center" @click="closeModal">
              Cancel
            </FormButton>
          </div>
        </div>
      </div>
    </template>
  </Sidebar>
</template>

<style>
.schedule-type-dropdown-menu {
  min-width: 100% !important;
  width: 100% !important;
}

.flow-dropdown-menu {
  min-width: 100% !important;
  width: 100% !important;
}
</style>
